import { useLazyQuery } from "@apollo/client";
import { useMemo } from "react";

import useTypedContext from "hooks/useTypedContext";
import FlashContext from "components/FlashMessages/FlashContext";

import {
  SEARCH_MANAGED_ENTITIES_GROUP_BY_STACKS,
  SEARCH_STACK_MANAGED_ENTITIES_GROUP_BY_STACKS,
} from "./gql";

const useIacManagementStackGroups = (parentStackId?: string) => {
  const { onError } = useTypedContext(FlashContext);
  const [
    accountLazyFetch,
    { data: accountData, previousData: accountPreviousData, loading: accountLoading },
  ] = useLazyQuery(SEARCH_MANAGED_ENTITIES_GROUP_BY_STACKS, {
    onError,
  });

  const [
    stackLazyFetch,
    { data: stackData, previousData: stackPreviousData, loading: stackLoading },
  ] = useLazyQuery(SEARCH_STACK_MANAGED_ENTITIES_GROUP_BY_STACKS, {
    onError,
  });

  const groups = useMemo(() => {
    if (parentStackId) {
      return (
        stackData?.stack?.searchManagedEntitiesGroupByStacks?.edges.map((edge) => edge.node) ||
        stackPreviousData?.stack?.searchManagedEntitiesGroupByStacks?.edges.map(
          (edge) => edge.node
        ) ||
        []
      );
    } else {
      return (
        accountData?.searchManagedEntitiesGroupByStacks?.edges.map((edge) => edge.node) ||
        accountPreviousData?.searchManagedEntitiesGroupByStacks?.edges.map((edge) => edge.node) ||
        []
      );
    }
  }, [
    parentStackId,
    stackData?.stack?.searchManagedEntitiesGroupByStacks?.edges,
    stackPreviousData?.stack?.searchManagedEntitiesGroupByStacks?.edges,
    accountData?.searchManagedEntitiesGroupByStacks?.edges,
    accountPreviousData?.searchManagedEntitiesGroupByStacks?.edges,
  ]);

  const accountDataIsEmpty =
    accountData &&
    "searchManagedEntitiesGroupByStacks" in accountData &&
    !groups.length &&
    !accountLoading;
  const stackDataIsEmpty =
    stackData &&
    "searchManagedEntitiesGroupByStacks" in stackData &&
    !groups.length &&
    !stackLoading;

  const endCursor = parentStackId
    ? stackData?.stack?.searchManagedEntitiesGroupByStacks?.pageInfo.endCursor
    : accountData?.searchManagedEntitiesGroupByStacks?.pageInfo.endCursor;
  const startCursor = parentStackId
    ? stackData?.stack?.searchManagedEntitiesGroupByStacks?.pageInfo.startCursor
    : accountData?.searchManagedEntitiesGroupByStacks?.pageInfo.startCursor;

  return {
    lazyFetch: parentStackId ? stackLazyFetch : accountLazyFetch,
    groups,
    loading: parentStackId ? stackLoading : accountLoading,
    endCursor,
    startCursor,
    dataIsEmpty: parentStackId ? stackDataIsEmpty : accountDataIsEmpty,
  };
};

export default useIacManagementStackGroups;
